import React, { useMemo, useState, useCallback, useRef } from 'react';
import { CalcIcon, ChartIcon } from '../icons/Icons';

function parseTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h + m / 60;
}

function formatHourLabel(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h >= 24) return `${String(h - 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function getActiveBanks(banya) {
  if (!banya || !banya.people) return [];
  const start = parseTime(banya.startTime);
  let end = parseTime(banya.endTime);
  if (end <= start) end += 24;
  const pt = banya.people.map(p => {
    let a = parseTime(p.arrivedAt);
    let l = parseTime(p.leftAt);
    if (l <= a) l += 24;
    if (a < start) a = start;
    if (l > end) l = end;
    return { ...p, arrivedAtNum: a, leftAtNum: l };
  });
  const evts = [];
  evts.push({ time: start, type: 'start' });
  evts.push({ time: end, type: 'end' });
  pt.forEach(p => { evts.push({ time: p.arrivedAtNum, type: 'arrive', personId: p.id }); evts.push({ time: p.leftAtNum, type: 'leave', personId: p.id }); });
  evts.sort((a, b) => a.time - b.time);
  const banks = [];
  let curPeople = [];
  let curTime = start;
  for (const ev of evts) {
    if (ev.time === curTime) {
      if (ev.type === 'arrive') { const pp = pt.find(x => x.id === ev.personId); if (pp) curPeople.push(pp); }
      else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
      continue;
    }
    if (ev.time > curTime) {
      const dur = ev.time - curTime;
      if (curPeople.length > 0) banks.push({ from: curTime, to: ev.time, dur, people: [...curPeople] });
      curTime = ev.time;
      if (ev.type === 'arrive') { const pp = pt.find(x => x.id === ev.personId); if (pp) curPeople.push(pp); }
      else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
    }
  }
  return banks;
}

const colors = [
  { bg: '#ff8a80' },
  { bg: '#82b1ff' },
  { bg: '#b9f6ca' },
  { bg: '#ffd740' },
  { bg: '#e0b0ff' },
  { bg: '#80deea' },
  { bg: '#ffab91' },
  { bg: '#aed581' },
  { bg: '#f48fb1' },
  { bg: '#9fa8da' },
];

export default function ShareScreen({ banya }) {
  const [activeTab, setActiveTab] = React.useState('calc');
  const [hoverPct, setHoverPct] = useState(null);
  const [selectedAward, setSelectedAward] = useState(null);
  const timelineRef = useRef(null);
  const closeAwardHint = useCallback(() => setSelectedAward(null), []);

  const handlePointerMove = useCallback((e) => {
    const el = timelineRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = (x / rect.width) * 100;
    setHoverPct(Math.max(0, Math.min(100, pct)));
  }, []);

  const handlePointerLeave = useCallback(() => {
    setHoverPct(null);
  }, []);

  const ganttData = useMemo(() => {
    if (!banya) return null;
    const { startTime, endTime, people } = banya;
    const start = parseTime(startTime);
    let end = parseTime(endTime);
    if (end <= start) end += 24;
    const totalMinutes = (end - start) * 60;

    const rows = people.map((p) => {
      let a = parseTime(p.arrivedAt);
      let l = parseTime(p.leftAt);
      if (l <= a) l += 24;
      if (a < start) a = start;
      if (l > end) l = end;
      return {
        id: p.id,
        name: p.name,
        emoji: p.emoji,
        arrivedAt: p.arrivedAt,
        leftAt: p.leftAt,
        arrivedMinutes: Math.round((a - start) * 60),
        durationMinutes: Math.round((l - a) * 60),
        leftPct: ((a - start) / (end - start)) * 100,
        widthPct: ((l - a) / (end - start)) * 100,
      };
    });

    const totalSlots = Math.ceil(totalMinutes / 30);
    const labels = [];
    for (let i = 0; i <= totalSlots; i++) {
      const h = start + (i * 30) / 60;
      if (h <= end) {
        labels.push({ time: h, label: formatHourLabel(h), leftPct: ((h - start) / (end - start)) * 100 });
      }
    }
    return { start, end, rows, labels };
  }, [banya]);

  const calculation = useMemo(() => {
    if (!banya) return null;
    const { startTime, endTime, hourlyRate, broomCost, people } = banya;
    const start = parseTime(startTime);
    let end = parseTime(endTime);
    if (end <= start) end += 24;
    const totalHours = end - start;
    if (totalHours <= 0) return null;

    const totalBanyaCost = hourlyRate * totalHours;
    const totalCost = totalBanyaCost + broomCost;
    const costPerHour = totalCost / totalHours;

    const personTimes = people.map((p) => {
      let a = parseTime(p.arrivedAt);
      let l = parseTime(p.leftAt);
      if (l <= a) l += 24;
      if (a < start) a = start;
      if (l > end) l = end;
      return { ...p, arrivedAtNum: a, leftAtNum: l };
    });

    const events = [];
    events.push({ time: start, type: 'start' });
    events.push({ time: end, type: 'end' });
    personTimes.forEach((p) => {
      events.push({ time: p.arrivedAtNum, type: 'arrive', personId: p.id });
      events.push({ time: p.leftAtNum, type: 'leave', personId: p.id });
    });
    events.sort((a, b) => a.time - b.time);

    const banks = [];
    let currentPeople = [];
    let currentTime = start;

    for (const event of events) {
      if (event.time === currentTime) {
        if (event.type === 'arrive') {
          const pp = personTimes.find((x) => x.id === event.personId);
          if (pp) currentPeople.push(pp);
        } else if (event.type === 'leave') {
          currentPeople = currentPeople.filter((x) => x.id !== event.personId);
        }
        continue;
      }
      if (event.time > currentTime) {
        const duration = event.time - currentTime;
        if (currentPeople.length > 0) {
          banks.push({ from: currentTime, to: event.time, duration, people: [...currentPeople] });
        }
        currentTime = event.time;
      }
      if (event.type === 'arrive') {
        const pp = personTimes.find((x) => x.id === event.personId);
        if (pp) currentPeople.push(pp);
      } else if (event.type === 'leave') {
        currentPeople = currentPeople.filter((x) => x.id !== event.personId);
      }
    }

    const personTotals = {};
    personTimes.forEach((p) => { personTotals[p.id] = { name: p.name, emoji: p.emoji, total: 0 }; });
    banks.forEach((bank) => {
      const bankCost = costPerHour * bank.duration;
      const share = bankCost / bank.people.length;
      bank.people.forEach((p) => { personTotals[p.id].total += share; });
    });

    const totalCheck = Object.values(personTotals).reduce((sum, p) => sum + p.total, 0);

    const formatTimeFn = (hours) => {
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      if (h >= 24) return `${String(h - 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    const formatDuration = (hours) => {
      if (hours < 1) return `${Math.round(hours * 60)}мин`;
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      if (m === 0) return `${h}ч`;
      return `${h}ч ${m}мин`;
    };

    return {
      totalBanyaCost, totalCost, totalHours, costPerHour, banks,
      personTotals: Object.values(personTotals), totalCheck,
      personTimes, formatTime: formatTimeFn, formatDuration,
      startTime, endTime, hourlyRate, broomCost, people,
    };
  }, [banya]);

  // Расчёт ачивок
  const awardsData = useMemo(() => {
    if (!banya || !ganttData || !ganttData.rows || ganttData.rows.length === 0) return {};

    const { startTime, endTime, hourlyRate, broomCost, people } = banya;
    const start = parseTime(startTime);
    let end = parseTime(endTime);
    if (end <= start) end += 24;
    const totalHours = end - start;
    const totalBanyaCost = hourlyRate * totalHours;
    const totalCost = totalBanyaCost + broomCost;
    const costPerHour = totalCost / totalHours;

    const personTimes = people.map((p) => {
      let a = parseTime(p.arrivedAt);
      let l = parseTime(p.leftAt);
      if (l <= a) l += 24;
      if (a < start) a = start;
      if (l > end) l = end;
      return { ...p, arrivedAtNum: a, leftAtNum: l };
    });

    const evts = [];
    evts.push({ time: start, type: 'start' });
    evts.push({ time: end, type: 'end' });
    personTimes.forEach((p) => {
      evts.push({ time: p.arrivedAtNum, type: 'arrive', personId: p.id });
      evts.push({ time: p.leftAtNum, type: 'leave', personId: p.id });
    });
    evts.sort((a, b) => a.time - b.time);

    const banks = [];
    let curPeople = [];
    let curTime = start;
    for (const ev of evts) {
      if (ev.time === curTime) {
        if (ev.type === 'arrive') { const pp = personTimes.find(x => x.id === ev.personId); if (pp) curPeople.push(pp); }
        else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
        continue;
      }
      if (ev.time > curTime) {
        const dur = ev.time - curTime;
        if (curPeople.length > 0) banks.push({ from: curTime, to: ev.time, dur, people: [...curPeople] });
        curTime = ev.time;
        if (ev.type === 'arrive') { const pp = personTimes.find(x => x.id === ev.personId); if (pp) curPeople.push(pp); }
        else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
      }
    }

    const personCosts = {};
    personTimes.forEach((p) => { personCosts[p.id] = { total: 0 }; });
    banks.forEach((bank) => {
      const bankCost = costPerHour * bank.dur;
      const share = bankCost / bank.people.length;
      bank.people.forEach((p) => { personCosts[p.id].total += share; });
    });

    const maxOverlapPerPerson = {};
    personTimes.forEach((p) => { maxOverlapPerPerson[p.id] = 0; });
    banks.forEach((bank) => {
      const count = bank.people.length;
      bank.people.forEach((p) => {
        if (count > maxOverlapPerPerson[p.id]) maxOverlapPerPerson[p.id] = count;
      });
    });

    const durations = personTimes.map(p => ({ id: p.id, dur: p.leftAtNum - p.arrivedAtNum }));
    const arrivalTimes = personTimes.map(p => ({ id: p.id, time: p.arrivedAtNum }));
    const departureTimes = personTimes.map(p => ({ id: p.id, time: p.leftAtNum }));

    const minArrival = Math.min(...arrivalTimes.map(x => x.time));
    const maxArrival = Math.max(...arrivalTimes.map(x => x.time));
    const minDeparture = Math.min(...departureTimes.map(x => x.time));
    const minDuration = Math.min(...durations.map(x => x.dur));
    const minCost = Math.min(...Object.values(personCosts).map(x => x.total));

    const awards = {};

    people.forEach((p) => {
      const pt = personTimes.find(x => x.id === p.id);
      const dur = pt.leftAtNum - pt.arrivedAtNum;
      const cost = personCosts[p.id].total;
      const maxOverlap = maxOverlapPerPerson[p.id];
      const list = [];

      if (pt.leftAtNum === minDeparture) list.push({ title: 'первый у финиша', emoji: '🏁' });
      if (Math.abs(pt.arrivedAtNum - start) < 0.01 && Math.abs(pt.leftAtNum - end) < 0.01) list.push({ title: 'сторож', emoji: '🕛' });
      if (pt.arrivedAtNum === maxArrival) list.push({ title: 'самый поздний', emoji: '🐢' });
      const midStart = start + (end - start) / 2;
      if (pt.arrivedAtNum > start + 1/60 && pt.leftAtNum < end - 1/60 && pt.arrivedAtNum <= midStart && pt.leftAtNum >= midStart) list.push({ title: 'посредник', emoji: '🥕' });
      if (Math.abs(pt.arrivedAtNum - start) < 1 / 60) list.push({ title: 'пунктуальный', emoji: '🫡' });
      if (Math.abs(cost - minCost) < 0.01) list.push({ title: 'экономный', emoji: '💰' });
      if (Math.abs(dur - minDuration) < 0.01) list.push({ title: 'занятой', emoji: '🐗' });
      const maxOverlapOverall = Math.max(...Object.values(maxOverlapPerPerson));
      if (maxOverlap === maxOverlapOverall) list.push({ title: 'вайбовый', emoji: '🤩' });

      awards[p.id] = list;
    });

    return awards;
  }, [banya, ganttData]);

  if (!banya) {
    return <div className="calc-section"><div className="calc-card"><p>Нет данных</p></div></div>;
  }

  const fmt = (n) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <span className="header-logo">Банькулятор</span>
        </div>
        <nav className="header-nav">
          <button className={activeTab === 'calc' ? 'active' : ''} onClick={() => setActiveTab('calc')}>
            <CalcIcon color={activeTab === 'calc' ? '#000000' : '#bbbbbb'} />
          </button>
          <button className={activeTab === 'stats' ? 'active' : ''} onClick={() => setActiveTab('stats')}>
            <ChartIcon color={activeTab === 'stats' ? '#000000' : '#bbbbbb'} />
          </button>
        </nav>
      </header>

      {(banya.name || banya.date) && (
        <div className="share-info">
          {banya.name && <h2>{banya.name}</h2>}
          {banya.date && <p>{new Date(banya.date).toLocaleDateString('ru-RU')}</p>}
        </div>
      )}

      {activeTab === 'stats' && ganttData && (
        <div className="calc-section">
          <div className="stats-gantt-wrapper">
        {/* Левая колонка с именами (фиксированная) */}
        <div className="stats-gantt-sidebar">
          {ganttData.rows.map((row, idx) => {
            const c = colors[idx % colors.length];
            return (
              <div className="stats-gantt-name-row" key={row.id}>
                <span className="stats-name-label"
                  style={{ backgroundColor: c.bg }}
                ><span className="stats-name-label-inner"><span className="emoji-inline">{row.emoji || '😎'}</span><span>{row.name}</span></span></span>
              </div>
            );
          })}
        </div>

        {/* Область с таймлайном */}
        <div className="stats-gantt-timeline-wrapper"
          ref={timelineRef}
          onMouseMove={handlePointerMove}
          onMouseLeave={handlePointerLeave}
          onTouchMove={(e) => { e.preventDefault(); const t = e.touches[0]; handlePointerMove({ clientX: t.clientX, clientY: t.clientY }); }}
          onTouchStart={(e) => { const t = e.touches[0]; handlePointerMove({ clientX: t.clientX, clientY: t.clientY }); }}
          onTouchEnd={handlePointerLeave}
          style={{ position: 'relative', cursor: 'crosshair' }}
        >
          {hoverPct !== null && (() => {
            const { start, end } = ganttData;
            const timeVal = start + (end - start) * (hoverPct / 100);
            const h = Math.floor(timeVal);
            const m = Math.round((timeVal - h) * 60);
            const timeLabel = (h >= 24 ? String(h - 24).padStart(2, '0') : String(h).padStart(2, '0')) + ':' + String(m).padStart(2, '0');
            return (
              <>
                <div className="gantt-crosshair-line" style={{ left: `${hoverPct}%` }} />
              </>
            );
          })()}

          {/* Строки с барами */}
          <div className="stats-gantt-rows">
            {ganttData.rows.map((row, idx) => {
              const c = colors[idx % colors.length];
              return (
                <div className="stats-gantt-row" key={row.id}>
                  <div
                    className="stats-gantt-bar"
                    style={{
                      left: `${row.leftPct}%`,
                      width: `${row.widthPct}%`,
                      backgroundColor: c.bg,
                    }}
                    title={`${row.name}: ${row.arrivedAt}—${row.leftAt} (${Math.floor(row.durationMinutes / 60)}ч ${row.durationMinutes % 60}мин)`}
                  >
                    {row.widthPct > 15 && (
                      <span className="stats-gantt-bar-text">
                        {row.arrivedAt}—{row.leftAt}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {hoverPct !== null && (() => {
            const { start, end } = ganttData;
            const timeVal = start + (end - start) * (hoverPct / 100);
            const h = Math.floor(timeVal);
            const m = Math.round((timeVal - h) * 60);
            const timeHours = h + m / 60;
            const timeLabel = (h >= 24 ? String(h - 24).padStart(2, '0') : String(h).padStart(2, '0')) + ':' + String(m).padStart(2, '0');
            // Find people who are in the bath at this moment
            const activeEmojis = ganttData.rows
              .filter(row => {
                const a = parseTime(row.arrivedAt);
                let l = parseTime(row.leftAt);
                if (l <= a) l += 24;
                return timeHours >= a && timeHours <= l;
              })
              .map(row => row.emoji || '😎');
            const emojiStr = activeEmojis.length > 0 ? activeEmojis.join('') : '—';
            return (
              <div className="gantt-crosshair-hint" style={{ left: `${hoverPct}%` }}>
                <div className="hint-time"><span className="hint-label">Время:</span> {timeLabel}{(() => {
                  const banks = getActiveBanks(banya);
                  const bank = banks.find(b => timeHours >= b.from && timeHours < b.to);
                  return bank ? <><span className="hint-bank-sep"> · </span><span className="hint-bank-name">Банк {banks.indexOf(bank) + 1}</span></> : null;
                })()}</div>
                <div className="hint-vibe-row"><span className="hint-label">Вайб:</span> <span className="hint-emojis">{emojiStr}</span></div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Люди */}
      {banya && banya.people && banya.people.length > 0 && (
        <div className="stats-people-grid">
          {banya.people.map((person, idx) => {
            const c = colors[idx % colors.length];
            const pAwards = awardsData[person.id] || [];
            return (
              <div className="stats-people-item" key={person.id}>
                <div className="stats-people-emoji">
                  {person.emoji || '😎'}
                </div>
                <div className="stats-people-name" style={{ backgroundColor: c.bg }}>
                  {person.name}
                </div>
                <div className="stats-people-card">
                  {(() => {
                    if (pAwards.length === 0) return <span className="stats-no-awards">—</span>;
                    return pAwards.map((a, i) => (
                      <div className="stats-award-row" key={i} onClick={() => setSelectedAward({ ...a, personName: person.name })}>
                        <span className="stats-award-emoji">{a.emoji}</span>
                        <span className="stats-award-title">{a.title}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}
        </div>
      )}{activeTab === 'calc' && calculation && (
        <div className="calc-section">
          <div className="calc-card">
            <h3>Сводка</h3>
            {calculation.personTotals.map((p, idx) => {
              const personCalc = calculation.personTimes.find(pt => pt.name === p.name);
              const c = colors[idx % colors.length];
              return (
                <div className="person-result" key={p.name}>
                  <div className="person-row-top">
                    <span className="stats-name-label" style={{ backgroundColor: c.bg }}><span className="stats-name-label-inner"><span className="emoji-inline">{p.emoji || '😎'}</span><span>{p.name}</span></span></span>
                  </div>
                  <div className="person-row-bottom">
                    <span className="person-time-col"><span className="person-time">{personCalc ? personCalc.arrivedAt + '—' + personCalc.leftAt : ''}</span></span>
                    <span className="person-duration-col"><span className="person-duration">{personCalc ? calculation.formatDuration(personCalc.leftAtNum - personCalc.arrivedAtNum) : ''}</span></span>
                    <span className="person-amount-col"><span className="amount">{fmt(p.total)} ₽</span></span>
                  </div>
                  {(() => {
                    const personAwards = awardsData[p.id] || [];
                    if (personAwards.length === 0) return null;
                    return (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {personAwards.map((a, i) => (
                          <span key={i} className="stats-award-row" onClick={() => setSelectedAward({ ...a, personName: p.name })} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', padding: '1px 4px', fontSize: 12 }}>
                            <span className="stats-award-emoji" style={{ fontSize: 14 }}>{a.emoji}</span>
                            <span className="stats-award-title" style={{ color: '#555' }}>{a.title}</span>
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
            <div className="row total mt-8">
              <span>Всего денег:</span><span>{fmt(calculation.totalCheck)} ₽</span>
            </div>
            <div className="row total total-time"><span>Общее время:</span><span className="mono">{calculation.startTime}—{calculation.endTime} ч</span></div>
          </div>

          <div className="calc-card">
            <h3>Деньги</h3>
            <div className="row"><span className="label">Стоимость часа</span><span>{calculation.hourlyRate.toLocaleString('ru-RU')} ₽</span></div>
            <div className="row">
              <span className="label">Стоимость бани</span>
              <span>{calculation.hourlyRate.toLocaleString('ru-RU')} ₽ × {calculation.formatDuration(calculation.totalHours)} = {calculation.totalBanyaCost.toLocaleString('ru-RU')} ₽</span>
            </div>
            <div className="row"><span className="label">Стоимость веника</span><span>{calculation.broomCost.toLocaleString('ru-RU')} ₽</span></div>
            <div className="row total"><span>Общий расход</span><span>{calculation.totalCost.toLocaleString('ru-RU')} ₽</span></div>
            <div className="row"><span className="label">Расход в час</span><span>{fmt(calculation.costPerHour)} ₽</span></div>
          </div>

          <div className="calc-card">
            <h3>Расчёт</h3>
            {calculation.banks.map((bank, idx) => {
              const colors_local = colors; // use the same colors
              return (
                <div className="bank-section" key={idx}>
                  <div className="bank-title-row">
                    <h4>Банк {idx + 1}</h4>
                    <div className="bank-header"><span>{calculation.formatDuration(bank.duration)}</span><span className="separator-dot" /><span>{calculation.formatTime(bank.from)}—{calculation.formatTime(bank.to)}</span></div>
                  </div>
                  <div className="bank-item">
                    <div className="bank-people">
                      {bank.people.map((p, pi, arr) => {
                        const pc = colors[calculation.people.findIndex(pp => pp.id === p.id) % colors.length];
                        return (
                          <span key={p.id} className="inline-flex-center">
                            <span className="badge-name-sm" style={{ backgroundColor: pc.bg }}><span className="emoji-inline">{p.emoji || '😎'}</span>{p.name}</span>
                          </span>
                        );
                      })}
                    </div>
                    <div className="bank-calculation">
                      {fmt(calculation.costPerHour)} ₽ × {calculation.formatDuration(bank.duration)} / {bank.people.length}чел = <strong>{fmt((calculation.costPerHour * bank.duration) / bank.people.length)} ₽</strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedAward && (
        <div className="stats-award-overlay" onClick={closeAwardHint}>
          <div className="stats-award-hint">
            {(function() {
              const descriptions = {
                'первый у финиша': selectedAward.personName + ' покинул баню раньше всех',
                'сторож': selectedAward.personName + ' был в бане всё время от начала до конца',
                'самый поздний': selectedAward.personName + ' пришёл в баню позже всех',
                'посредник': selectedAward.personName + ' находился в бане в середине сессии',
                'пунктуальный': selectedAward.personName + ' пришёл ровно к началу',
                'экономный': selectedAward.personName + ' потратил меньше всех денег',
                'занятой': selectedAward.personName + ' провёл в бане меньше всех времени',
                'вайбовый': selectedAward.personName + ' застал наибольшее количество присутствующих одновременно',
              };
              return descriptions[selectedAward.title] || '';
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
